import { defineCoach } from '../../types';
import { PERMISSIONS, canAll } from '../tasks/access';

/**
 * C01 — Requester: "Create your first request" (TASK §11 Layer B, CP4).
 *
 * Catalogue → search an approved item → open it → its terms → quantity → the
 * hand-over → the person adds it → their cart → lines, details,
 * budget → the person opens the review → controls and route → the hand-over →
 * the person submits with the page's own button → the request: status, next
 * action, approvers → Finish.
 *
 * For a seat that may browse the catalogue AND raise a request — exactly the
 * seats the catalogue's Add control and the Cart exist for. Works with whatever
 * approved item the seat can see: it names none, and picks none.
 *
 * ── What the coach does, and what it never does ────────────────────────────
 * It navigates only to the catalogue (a safe route, GET only), and only at its
 * first step. It never opens an item, never adds, never opens the cart (whose
 * opening validates), never reviews, never submits: the PERSON does each, in
 * the ordinary page, after a hand-over card for every change of data. Each
 * wait ends only on the authoritative outcome:
 * - the add: `cart.line-added`, the server's acceptance (AddToRequest's own
 *   mutation), wherever the person added from;
 * - the cart: its page, with its lines as `GET /cart` returned;
 * - the review: the review's commit, as the server's validation decided it;
 * - the submission: `request.submitted` on the bus, AND the request's own page,
 *   AND its status as the server returned it — not a draft.
 * A refusal keeps the step; the copy says the page says why.
 *
 * Copy: `guidance.coaches.C01.*` (src/guidance/locales) — Arabic is DRAFT. The
 * requester's editable draft is called their cart, as on the page (OBS-005,
 * provisional option A, pending the Product Owner with Design and the Brand
 * Owner's R-041 entry for «Cart — السلة»).
 */
const SUBMITTED = [
  'pending_approval',
  'approved',
  'returned',
  'rejected',
  'cancelled',
  'procurement_review',
  'sourcing_required',
] as const;

export const C01 = defineCoach({
  id: 'C01',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.coaches.C01.title',
  summaryKey: 'guidance.coaches.C01.summary',
  eligible: (ctx) =>
    ctx.portal === 'tenant' && canAll(ctx, PERMISSIONS.catalogueBrowse, PERMISSIONS.requestCreate),
  steps: [
    {
      id: 'c01-welcome',
      kind: 'explain',
      page: 'tenant.catalogue',
      route: 'tenant.catalogue',
      anchor: null,
      titleKey: 'guidance.coaches.C01.welcome.title',
      bodyKey: 'guidance.coaches.C01.welcome.body',
    },
    {
      id: 'c01-search',
      kind: 'explain',
      page: 'tenant.catalogue',
      anchor: 'tenant.catalogue.filters',
      titleKey: 'guidance.coaches.C01.search.title',
      bodyKey: 'guidance.coaches.C01.search.body',
    },
    {
      // Opening an item is reading: no hand-over, and the route decides.
      id: 'c01-open-item',
      kind: 'await',
      page: 'tenant.catalogue',
      anchor: 'tenant.catalogue.results',
      until: { route: 'tenant.catalogue-item' },
      titleKey: 'guidance.coaches.C01.openItem.title',
      bodyKey: 'guidance.coaches.C01.openItem.body',
    },
    {
      id: 'c01-terms',
      kind: 'explain',
      page: 'tenant.catalogue-item',
      anchor: 'tenant.item.order-panel',
      titleKey: 'guidance.coaches.C01.terms.title',
      bodyKey: 'guidance.coaches.C01.terms.body',
    },
    {
      id: 'c01-quantity',
      kind: 'explain',
      page: 'tenant.catalogue-item',
      anchor: 'tenant.item.quantity',
      titleKey: 'guidance.coaches.C01.quantity.title',
      bodyKey: 'guidance.coaches.C01.quantity.body',
    },
    {
      id: 'c01-add-handoff',
      kind: 'handoff',
      page: 'tenant.catalogue-item',
      anchor: 'tenant.item.add',
      titleKey: 'guidance.coaches.C01.add.title',
      bodyKey: 'guidance.coaches.C01.add.body',
      consequenceKey: 'guidance.coaches.C01.add.consequence',
    },
    {
      id: 'c01-add',
      kind: 'await',
      page: 'tenant.catalogue-item',
      anchor: 'tenant.item.add',
      until: { outcome: 'cart.line-added' },
      disabledExplainKey: 'guidance.coaches.C01.add.disabled',
      titleKey: 'guidance.coaches.C01.add.title',
      bodyKey: 'guidance.coaches.C01.add.waiting',
    },
    {
      // The confirmation's own link, or Cart in the navigation.
      id: 'c01-open-cart',
      kind: 'await',
      page: 'tenant.catalogue-item',
      anchor: ['tenant.item.added', 'tenant.nav.cart'],
      until: { route: 'tenant.cart', probe: { anchor: 'tenant.cart.lines', in: ['has-items'] } },
      titleKey: 'guidance.coaches.C01.openCart.title',
      bodyKey: 'guidance.coaches.C01.openCart.body',
    },
    {
      id: 'c01-lines',
      kind: 'explain',
      page: 'tenant.cart',
      anchor: 'tenant.cart.lines',
      titleKey: 'guidance.coaches.C01.lines.title',
      bodyKey: 'guidance.coaches.C01.lines.body',
    },
    {
      id: 'c01-details',
      kind: 'explain',
      page: 'tenant.cart',
      anchor: 'tenant.cart.details',
      titleKey: 'guidance.coaches.C01.details.title',
      bodyKey: 'guidance.coaches.C01.details.body',
    },
    {
      // The budget shows once the server's validation has answered.
      id: 'c01-budget',
      kind: 'explain',
      page: 'tenant.cart',
      anchor: 'tenant.cart.budget',
      absent: { present: 'tenant.cart.lines', explainKey: 'guidance.coaches.C01.budget.absent' },
      titleKey: 'guidance.coaches.C01.budget.title',
      bodyKey: 'guidance.coaches.C01.budget.body',
    },
    {
      // Opening the review is reading (it validates again, writing nothing).
      id: 'c01-review',
      kind: 'await',
      page: 'tenant.cart',
      anchor: 'tenant.cart.review',
      until: { probe: { anchor: 'tenant.cart-review.submit', in: ['ready', 'blocked'] } },
      titleKey: 'guidance.coaches.C01.review.title',
      bodyKey: 'guidance.coaches.C01.review.body',
    },
    {
      id: 'c01-controls',
      kind: 'explain',
      page: 'tenant.cart',
      anchor: 'tenant.cart-review.controls',
      titleKey: 'guidance.coaches.C01.controls.title',
      bodyKey: 'guidance.coaches.C01.controls.body',
    },
    {
      id: 'c01-route',
      kind: 'explain',
      page: 'tenant.cart',
      anchor: 'tenant.cart-review.route',
      titleKey: 'guidance.coaches.C01.route.title',
      bodyKey: 'guidance.coaches.C01.route.body',
    },
    {
      // The consequence in the review's own words (GS-08 `request.submitExplains`).
      id: 'c01-submit-handoff',
      kind: 'handoff',
      page: 'tenant.cart',
      anchor: 'tenant.cart-review.submit',
      titleKey: 'guidance.coaches.C01.submit.title',
      bodyKey: 'guidance.coaches.C01.submit.body',
      consequenceKey: 'request.submitExplains',
    },
    {
      id: 'c01-submit',
      kind: 'await',
      page: 'tenant.cart',
      anchor: 'tenant.cart-review.submit',
      until: {
        outcome: 'request.submitted',
        route: 'tenant.request',
        probe: { anchor: 'tenant.request.status', in: SUBMITTED },
      },
      disabledExplainKey: 'guidance.coaches.C01.submit.disabled',
      titleKey: 'guidance.coaches.C01.submit.title',
      bodyKey: 'guidance.coaches.C01.submit.waiting',
    },
    {
      // Said only now — after the server's answer, the page and the status agree.
      id: 'c01-status',
      kind: 'explain',
      page: 'tenant.request',
      anchor: 'tenant.request.status',
      titleKey: 'guidance.coaches.C01.status.title',
      bodyKey: 'guidance.coaches.C01.status.body',
    },
    {
      id: 'c01-next-action',
      kind: 'explain',
      page: 'tenant.request',
      anchor: 'tenant.request.next-action',
      titleKey: 'guidance.coaches.C01.nextAction.title',
      bodyKey: 'guidance.coaches.C01.nextAction.body',
    },
    {
      // A request the rules approve without a person has no approvers to list.
      id: 'c01-approvers',
      kind: 'explain',
      page: 'tenant.request',
      anchor: 'tenant.request.approvers',
      absent: {
        present: 'tenant.request.status',
        explainKey: 'guidance.coaches.C01.approvers.absent',
      },
      titleKey: 'guidance.coaches.C01.approvers.title',
      bodyKey: 'guidance.coaches.C01.approvers.body',
    },
    {
      id: 'c01-finish',
      kind: 'finish',
      page: 'tenant.request',
      anchor: null,
      titleKey: 'guidance.coaches.C01.finish.title',
      bodyKey: 'guidance.coaches.C01.finish.body',
    },
  ],
});
